"use strict";

var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};
Object.defineProperty(exports, "__esModule", {
  value: true
});
const lodash_1 = __importDefault(require("lodash"));
const TextExpander_1 = __importDefault(require("./TextExpander"));
const chart_helpers_1 = require("../../util/chart.helpers");
require("./SourcesAndFootnotes.scss");
class SourcesAndFootnotes {
  constructor(container) {
    this.container = (0, chart_helpers_1.getD3Selection)(container);
    if (this.container === null) {
      throw new Error('[SourcesAndFootnotes] Constructor argument is not an Element or D3 selection, or two instances of D3 are being used');
    }
    this.container.classed('chart-saf', true);
    this.initGroups();
    this.hide();
  }
  initGroups() {
    const sourcesDiv = this.container.append('div').classed('saf-sources', true);
    this.sources = new TextExpander_1.default(sourcesDiv);
    this.sources.collapse();
    const footnotesDiv = this.container.append('div').classed('saf-footnotes', true);
    this.footnotes = new TextExpander_1.default(footnotesDiv);
    this.footnotes.collapse();
  }
  draw(data) {
    let {
      chartFootnotes,
      chartSources,
      hideMetricFootnotes
    } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const flatData = data.flatMap(d => d);
    const {
      sources,
      summary: sourceSummary
    } = SourcesAndFootnotes.getSources(flatData, chartSources);
    const {
      footnotes,
      summary: footnoteSummary
    } = SourcesAndFootnotes.getFootnotes(flatData, chartFootnotes, hideMetricFootnotes);
    if (sources.length > 0) {
      this.drawSources(sources, sourceSummary);
      this.sources.show();
    } else {
      this.sources.hide();
    }
    if (footnotes.length > 0) {
      this.drawFootnotes(footnotes, footnoteSummary);
      this.footnotes.show();
    } else {
      this.footnotes.hide();
    }
    this.container.style('display', null);
  }
  drawSources(sources, summary) {
    this.sources.setTitle('Sources');
    this.sources.summary.text(summary);
    const longSources = this.sources.content.selectAll('div.source-long').data(sources, d => d[0]).join('div').classed('source-long', true);
    longSources.selectAll('div.long-title').data(d => [d[0]]).join('div').classed('long-title', true).text(d => d);
    const entries = longSources.selectAll('div.long-entry').data(d => d[1]).join('div').classed('long-entry', true);
    const entryLink = entries.selectAll('a.long-link').data(d => [d]).join('a').classed('long-link', true).attr('href', d => d.url ? d.url : '#').attr('target', '_blank');
    entryLink.selectAll('span.long-link-text').data(d => [d]).join('span').classed('long-link-text', true).text(d => d.name);
    entryLink.selectAll('div.external-icon').data(d => [d]).join('div').classed('external-icon', true);
    entries.selectAll('span.long-updated').data(d => [d]).join('span').classed('long-updated', true).text(d => d.lastupdated ? `(updated ${d.lastupdated})` : '');
  }
  drawFootnotes(footnotes) {
    let summary = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
    this.footnotes.setTitle('Footnotes');
    const summaryText = summary && summary.length > 50 ? `${summary.substr(0, 50)}...` : summary;
    this.footnotes.summary.text(summaryText);
    const longFootnotes = this.footnotes.content.selectAll('div.footnote-long').data(footnotes, d => d[0]).join('div').classed('footnote-long', true);
    longFootnotes.selectAll('div.long-title').data(d => [d[0]]).join('div').classed('long-title', true).text(d => d);
    longFootnotes.selectAll('div.long-data').data(d => d[1]).join('div').classed('long-data', true).text(d => d.data);
  }
  static getSources(data) {
    let chartSources = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
    const metricSources = data.flatMap(d => {
      var _d$metric$metadata;
      if (!d.metric) return [];
      if (d.metric.sources) {
        return d.metric.sources.map(source => ({
          ...source,
          // show URL if source is missing a name
          name: source.name || source.url || ''
        }));
      }
      if ((_d$metric$metadata = d.metric.metadata) !== null && _d$metric$metadata !== void 0 && _d$metric$metadata.source) {
        const {
          metadata
        } = d.metric;
        return [{
          ...metadata,
          agencyname: metadata.agencyname || metadata.source,
          // show URL if source is missing a name
          name: metadata.sourceName || metadata.sourceURL || '',
          url: metadata.sourceURL
        }];
      }
      return [];
    });
    const sources = (0, lodash_1.default)(chartSources).concat(metricSources).uniqBy(source => source.url).groupBy(source => source.agencyname).entries().value();
    return {
      sources,
      summary: `${sources.map(d => d[0]).join('; ')}.`
    };
  }
  // eslint-disable-next-line default-param-last
  static getFootnotes(data) {
    var _footnotes$;
    let chartFootnotes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
    let hideMetricFootnotes = arguments.length > 2 ? arguments[2] : undefined;
    const metricFootnotes = hideMetricFootnotes ? [] : (0, lodash_1.default)(data).flatMap(d => {
      var _d$metric$metadata2, _d$metric;
      if (!d.metric) return [];
      if ((_d$metric$metadata2 = d.metric.metadata) !== null && _d$metric$metadata2 !== void 0 && _d$metric$metadata2.footnote) {
        return [{
          metric: d.metric.name,
          data: d.metric.metadata.footnote
        }];
      }
      if (d !== null && d !== void 0 && (_d$metric = d.metric) !== null && _d$metric !== void 0 && _d$metric.datasets) {
        return d.metric.datasets.flatMap(set => {
          var _set$meta;
          return ((_set$meta = set.meta) === null || _set$meta === void 0 ? void 0 : _set$meta.map(m => ({
            ...m,
            metric: d.metric.name
          })).filter(m => m.type.toLowerCase() === 'footnote')) || [];
        });
      }
      return [];
    }).uniqBy(source => `${source.metric} | ${source.data}`).value();
    const footnotes = chartFootnotes.concat(metricFootnotes);
    return {
      footnotes: (0, lodash_1.default)(footnotes).groupBy(function () {
        let {
          title = '',
          metric = title
        } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
        return metric;
      }).entries().value(),
      summary: footnotes === null || footnotes === void 0 || (_footnotes$ = footnotes[0]) === null || _footnotes$ === void 0 ? void 0 : _footnotes$.data
    };
  }
  hide() {
    this.sources.hide();
    this.footnotes.hide();
  }
}
exports.default = SourcesAndFootnotes;